import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/foundation/Card';
import { useAuth } from '../auth/useAuth';

/**
 * Login experience for the Super Admin mobile+password flow, backed
 * by POST /api/representative/auth/login.
 *
 * Login goes through useAuth() only. The page does not know which
 * authentication provider is underneath it.
 */
export function LoginPage() {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(
    undefined,
  );

  const { login } = useAuth();
  const navigate = useNavigate();

  const validate = (): string | undefined => {
    if (!mobile.trim()) {
      return 'شماره موبایل را وارد کنید';
    }

    if (!password) {
      return 'رمز عبور را وارد کنید';
    }

    return undefined;
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    setError(undefined);
    setIsSubmitting(true);

    try {
      const result = await login(
        mobile.trim(),
        password,
      );

      if (result.ok) {
        navigate('/dashboard', {
          replace: true,
        });
      } else {
        // result.error carries the backend's own detail message
        // (401/403/423) when available — see
        // localAuthProvider.ts::extractErrorMessage.
        setError(
          result.error ??
            'اطلاعات ورود صحیح نیست',
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      dir="rtl"
      className="flex min-h-screen items-center justify-center bg-[var(--vania-background,#0b0d12)] px-4"
    >
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--vania-accent,#5b8cff)] text-xl font-bold text-white shadow-lg">
            V
          </div>

          <div className="text-lg font-semibold text-[var(--vania-text-primary,#f5f6f8)]">
            VaniaLabs
          </div>

          <div className="mt-1 text-sm text-[var(--vania-text-secondary,#a6acb8)]">
            پنل مدیر کل
          </div>
        </div>

        <Card className="w-full border border-[var(--vania-border,rgba(255,255,255,0.1))] bg-[var(--vania-surface-1,rgba(20,22,30,0.72))] p-6 shadow-2xl backdrop-blur-xl">
          <h1 className="mb-1 text-center text-lg font-semibold text-[var(--vania-text-primary,#f5f6f8)]">
            ورود مدیر کل
          </h1>

          <p className="mb-6 text-center text-sm text-[var(--vania-text-secondary,#a6acb8)]">
            با موبایل و رمز عبور وارد پنل مدیریت شوید.
          </p>

          <form
            onSubmit={handleSubmit}
            noValidate
            className="flex flex-col gap-4"
          >
            <label className="flex flex-col gap-1.5 text-right">
              <span className="text-xs text-[var(--vania-text-secondary,#a6acb8)]">
                موبایل
              </span>

              <input
                type="tel"
                dir="ltr"
                value={mobile}
                onChange={(event) =>
                  setMobile(event.target.value)
                }
                disabled={isSubmitting}
                autoComplete="tel"
                className="rounded-lg border border-[var(--vania-border,rgba(255,255,255,0.1))] bg-transparent px-3 py-2 text-sm text-[var(--vania-text-primary,#f5f6f8)] outline-none transition-colors focus:border-[var(--vania-accent,#5b8cff)] disabled:opacity-60"
                placeholder="0912xxxxxxx"
              />
            </label>

            <label className="flex flex-col gap-1.5 text-right">
              <span className="text-xs text-[var(--vania-text-secondary,#a6acb8)]">
                رمز عبور
              </span>

              <input
                type="password"
                dir="ltr"
                value={password}
                onChange={(event) =>
                  setPassword(event.target.value)
                }
                disabled={isSubmitting}
                autoComplete="current-password"
                className="rounded-lg border border-[var(--vania-border,rgba(255,255,255,0.1))] bg-transparent px-3 py-2 text-sm text-[var(--vania-text-primary,#f5f6f8)] outline-none transition-colors focus:border-[var(--vania-accent,#5b8cff)] disabled:opacity-60"
                placeholder="••••••••"
              />
            </label>

            {error && (
              <p
                role="alert"
                className="rounded-lg border border-[var(--vania-accent-rose,#ff6b81)] bg-[var(--vania-surface-2,rgba(255,255,255,0.06))] px-3 py-2 text-xs text-[var(--vania-accent-rose,#ff6b81)]"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center justify-center gap-2 rounded-lg bg-[var(--vania-accent,#5b8cff)] px-4 py-2.5 text-sm font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting && (
                <span
                  aria-hidden="true"
                  className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
                />
              )}

              {isSubmitting
                ? 'در حال ورود…'
                : 'ورود به پنل'}
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}
